import { watch } from "vue";
import { featureFlags } from "@/store/featureFlags.js";
import { authStore } from "@/store/auth.js";

// Drives FCM token registration off the runtime mobileNotifications flag and
// the signed-in user, replacing the one-shot onMounted check that raced the
// flag. Both inputs are runtime: the flag starts false and resolves from RTDB,
// and authStore.userId is restored asynchronously on cold launch, so this
// watches both and reacts.
//
// Two distinct lifetimes, so two latches:
//  - listenForTokenRotation() attaches a global FCM listener — attach it once
//    so switching accounts doesn't stack duplicate listeners.
//  - refreshTokenIfPermitted() writes notifications/tokens/{userId} and no-ops
//    without a userId, so it must (re-)run for each newly signed-in user:
//    the returning user on a cold launch (flag can resolve before auth), User
//    B after an in-app A -> B switch, and the same user after a sign-out that
//    deleted the device token. The per-user latch is cleared on sign-out so a
//    re-login of the same uid counts as a fresh registration.
//
// Push handlers are injected (from usePushNotifications) so the watch logic is
// unit-testable without the native messaging plugin. Returns the watch stop
// handle; call inside a component setup() so it is disposed on unmount.
export function setupPushRegistration({
  isSupported,
  listenForTokenRotation,
  refreshTokenIfPermitted,
}) {
  let rotationListenerAttached = false;
  let tokenRefreshedForUserId = null;
  return watch(
    () => [featureFlags.mobileNotifications, authStore.userId],
    ([enabled, userId]) => {
      if (!userId) tokenRefreshedForUserId = null;
      if (!enabled || !userId || !isSupported()) return;
      if (!rotationListenerAttached) {
        rotationListenerAttached = true;
        listenForTokenRotation();
      }
      if (userId !== tokenRefreshedForUserId) {
        tokenRefreshedForUserId = userId;
        refreshTokenIfPermitted().catch((e) =>
          console.error("FCM token refresh failed:", e)
        );
      }
    },
    { immediate: true }
  );
}
